import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertTriangle,
  Clock,
  Euro,
  Search,
  Filter,
  Mail,
  FileText,
  Gavel,
  Calendar,
  TrendingUp,
  Users,
  ChevronRight,
  Loader2,
  Plus,
  Upload,
  Download,
  Eye,
  MoreVertical,
  Send,
  History,
  Phone,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Voicemail,
  Pencil,
  Trash2,
  Copy,
  User,
  Building2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const RecoveryManagementPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [recoveryStats, setRecoveryStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBracket, setFilterBracket] = useState('all');
  
  // Action dialog state
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [actionType, setActionType] = useState('email');
  const [actionChannel, setActionChannel] = useState('email');
  const [actionSubject, setActionSubject] = useState('');
  const [actionContent, setActionContent] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [contactResult, setContactResult] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // History dialog state
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedClientHistory, setSelectedClientHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Template management
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'reminder_1',
    channel: 'email',
    subject: '',
    content: '',
    days_after_due: ''
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [uploadingPdf, setUploadingPdf] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overdueRes, statsRes, templatesRes] = await Promise.all([
        api.get('/recovery/overdue-invoices'),
        api.get('/recovery/stats'),
        api.get('/recovery/templates')
      ]);
      setOverdueInvoices(overdueRes.data.invoices || []);
      setStats(overdueRes.data.stats);
      setRecoveryStats(statsRes.data);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAction = async () => {
    if (!selectedInvoice) return;
    
    setSubmitting(true);
    try {
      await api.post('/recovery/actions', {
        invoice_id: selectedInvoice.id,
        action_type: actionType,
        channel: actionChannel,
        subject: actionSubject,
        message_content: actionContent,
        template_id: selectedTemplateId || null,
        notes: actionNotes,
        contact_result: contactResult || null
      });
      
      toast.success('Action de recouvrement enregistrée');
      setShowActionDialog(false);
      resetActionForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const resetActionForm = () => {
    setActionType('email');
    setActionChannel('email');
    setActionSubject('');
    setActionContent('');
    setActionNotes('');
    setContactResult('');
    setSelectedTemplateId('');
  };

  const loadClientHistory = async (clientId) => {
    setLoadingHistory(true);
    setShowHistoryDialog(true);
    try {
      const response = await api.get(`/recovery/client/${clientId}/history`);
      setSelectedClientHistory(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleUploadPdf = async (invoiceId, file) => {
    if (!file) return;
    
    setUploadingPdf({ ...uploadingPdf, [invoiceId]: true });
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post(`/service-invoices/${invoiceId}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Facture PDF uploadée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingPdf({ ...uploadingPdf, [invoiceId]: false });
    }
  };

  const openActionDialog = (invoice) => {
    setSelectedInvoice(invoice);
    resetActionForm();
    setShowActionDialog(true);
  };

  const applyTemplate = (template) => {
    if (!template || !selectedInvoice) return;
    
    // Replace variables in template
    let subject = template.subject;
    let content = template.content;
    
    const variables = {
      '{{client_name}}': selectedInvoice.client_name || '',
      '{{invoice_number}}': selectedInvoice.invoice_number || '',
      '{{amount}}': selectedInvoice.total_amount?.toLocaleString('fr-FR') || '0',
      '{{due_date}}': selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString('fr-FR') : '',
      '{{days_late}}': selectedInvoice.days_late?.toString() || '0',
      '{{interest_amount}}': selectedInvoice.interest_amount?.toLocaleString('fr-FR') || '0',
      '{{total_with_fees}}': selectedInvoice.total_with_fees?.toLocaleString('fr-FR') || '0',
      '{{date}}': new Date().toLocaleDateString('fr-FR')
    };
    
    Object.entries(variables).forEach(([key, value]) => {
      subject = subject.replace(new RegExp(key, 'g'), value);
      content = content.replace(new RegExp(key, 'g'), value);
    });
    
    setActionSubject(subject);
    setActionContent(content);
    setActionChannel(template.channel);
    setSelectedTemplateId(template.id);
  };

  // Template management functions
  const openTemplateDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        type: template.type,
        channel: template.channel,
        subject: template.subject,
        content: template.content,
        days_after_due: template.days_after_due?.toString() || ''
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        type: 'reminder_1',
        channel: 'email',
        subject: '',
        content: '',
        days_after_due: ''
      });
    }
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSavingTemplate(true);
    try {
      const data = {
        ...templateForm,
        days_after_due: templateForm.days_after_due ? parseInt(templateForm.days_after_due) : null,
        is_active: true
      };
      
      if (editingTemplate) {
        await api.put(`/recovery/templates/${editingTemplate.id}`, data);
        toast.success('Template mis à jour');
      } else {
        await api.post('/recovery/templates', data);
        toast.success('Template créé');
      }
      
      setShowTemplateDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await api.delete(`/recovery/templates/${templateId}`);
      toast.success('Template supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredInvoices = overdueInvoices.filter(inv => {
    const matchesSearch = 
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.service_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterBracket === 'all') return matchesSearch;
    
    const days = inv.days_late || 0;
    if (filterBracket === '1_30') return matchesSearch && days <= 30;
    if (filterBracket === '31_60') return matchesSearch && days > 30 && days <= 60;
    if (filterBracket === '61_90') return matchesSearch && days > 60 && days <= 90;
    if (filterBracket === '90_plus') return matchesSearch && days > 90;
    
    return matchesSearch;
  });

  const getAgeBadge = (days) => {
    if (days <= 30) return <Badge className="bg-yellow-100 text-yellow-700">{days}j</Badge>;
    if (days <= 60) return <Badge className="bg-orange-100 text-orange-700">{days}j</Badge>;
    if (days <= 90) return <Badge className="bg-red-100 text-red-700">{days}j</Badge>;
    return <Badge className="bg-red-600 text-white">{days}j</Badge>;
  };

  const actionTypeOptions = [
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'phone', label: 'Appel téléphonique', icon: Phone },
    { value: 'registered_mail', label: 'Courrier recommandé (AR)', icon: FileText },
    { value: 'formal_notice', label: 'Mise en demeure', icon: Gavel },
    { value: 'legal', label: 'Procédure contentieuse', icon: AlertTriangle }
  ];

  const contactResultOptions = [
    { value: 'answered', label: 'Répondu', icon: CheckCircle2 },
    { value: 'no_answer', label: 'Pas de réponse', icon: XCircle },
    { value: 'voicemail', label: 'Message vocal', icon: Voicemail },
    { value: 'refused', label: 'Refusé', icon: XCircle },
    { value: 'promise_to_pay', label: 'Promesse de paiement', icon: CheckCircle2 }
  ];

  const getActionIcon = (type) => {
    const icons = {
      email: Mail,
      phone: Phone,
      registered_mail: FileText,
      formal_notice: Gavel,
      legal: AlertTriangle,
      reminder: Mail,
      first_late_fee_applied: Euro
    };
    const Icon = icons[type] || MessageSquare;
    return <Icon className="w-4 h-4" />;
  };

  const getChannelLabel = (channel) => {
    const labels = {
      email: 'Email',
      phone: 'Téléphone',
      mail: 'Courrier',
      ar: 'Recommandé AR',
      registered_mail: 'Recommandé AR'
    };
    return labels[channel] || channel;
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        <Tabs defaultValue="invoices" className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3" data-testid="recovery-title">
                <AlertTriangle className="w-8 h-8 text-orange-600" />
                Recouvrement
              </h1>
              <p className="text-[#64748b] mt-1">
                Gestion des impayés, relances et frais de retard
              </p>
            </div>
            
            <TabsList>
              <TabsTrigger value="invoices">Factures impayées</TabsTrigger>
              <TabsTrigger value="templates">Modèles de messages</TabsTrigger>
            </TabsList>
          </div>

          {/* INVOICES TAB */}
          <TabsContent value="invoices">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Factures en retard</p>
                        <p className="text-2xl font-bold text-red-600">{stats.total_count}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Euro className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Montant impayé</p>
                        <p className="text-2xl font-bold">{stats.total_overdue_amount?.toLocaleString('fr-FR')}€</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Frais de retard</p>
                        <p className="text-2xl font-bold">{stats.total_late_fees?.toLocaleString('fr-FR')}€</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Clock className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total à récupérer</p>
                        <p className="text-2xl font-bold text-blue-600">{stats.total_with_fees?.toLocaleString('fr-FR')}€</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Age brackets overview */}
            {recoveryStats?.brackets && (
              <Card className="card mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Répartition par ancienneté</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                      <p className="text-sm text-yellow-700 mb-1">1-30 jours</p>
                      <p className="text-2xl font-bold text-yellow-800">{recoveryStats.brackets["1_30"]?.count || 0}</p>
                      <p className="text-sm text-yellow-600">{(recoveryStats.brackets["1_30"]?.amount || 0).toLocaleString('fr-FR')}€</p>
                    </div>
                    <div className="p-4 rounded-xl bg-orange-50 border border-orange-200">
                      <p className="text-sm text-orange-700 mb-1">31-60 jours</p>
                      <p className="text-2xl font-bold text-orange-800">{recoveryStats.brackets["31_60"]?.count || 0}</p>
                      <p className="text-sm text-orange-600">{(recoveryStats.brackets["31_60"]?.amount || 0).toLocaleString('fr-FR')}€</p>
                    </div>
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                      <p className="text-sm text-red-700 mb-1">61-90 jours</p>
                      <p className="text-2xl font-bold text-red-800">{recoveryStats.brackets["61_90"]?.count || 0}</p>
                      <p className="text-sm text-red-600">{(recoveryStats.brackets["61_90"]?.amount || 0).toLocaleString('fr-FR')}€</p>
                    </div>
                    <div className="p-4 rounded-xl bg-red-100 border border-red-300">
                      <p className="text-sm text-red-800 mb-1">+90 jours</p>
                      <p className="text-2xl font-bold text-red-900">{recoveryStats.brackets["90_plus"]?.count || 0}</p>
                      <p className="text-sm text-red-700">{(recoveryStats.brackets["90_plus"]?.amount || 0).toLocaleString('fr-FR')}€</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filters */}
            <Card className="card mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher par numéro, client ou service..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={filterBracket} onValueChange={setFilterBracket}>
                    <SelectTrigger className="w-full md:w-48">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filtrer par ancienneté" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les factures</SelectItem>
                      <SelectItem value="1_30">1-30 jours</SelectItem>
                      <SelectItem value="31_60">31-60 jours</SelectItem>
                      <SelectItem value="61_90">61-90 jours</SelectItem>
                      <SelectItem value="90_plus">+90 jours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Overdue Invoices Table */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              </div>
            ) : filteredInvoices.length === 0 ? (
              <Card className="card">
                <CardContent className="py-12 text-center">
                  <AlertTriangle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-green-700">Aucune facture en retard</h3>
                  <p className="text-muted-foreground">
                    Toutes les factures sont à jour !
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">Facture</th>
                        <th className="text-left p-4 font-medium text-slate-600">Client</th>
                        <th className="text-left p-4 font-medium text-slate-600">Service</th>
                        <th className="text-right p-4 font-medium text-slate-600">Montant</th>
                        <th className="text-center p-4 font-medium text-slate-600">Retard</th>
                        <th className="text-right p-4 font-medium text-slate-600">Total dû</th>
                        <th className="text-left p-4 font-medium text-slate-600">Dernière action</th>
                        <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((invoice) => (
                        <tr key={invoice.id} className="border-b hover:bg-slate-50 transition-colors">
                          <td className="p-4">
                            <span className="font-mono font-medium text-[#1a365d]">
                              {invoice.invoice_number}
                            </span>
                            <p className="text-xs text-slate-500 mt-1">
                              Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                            </p>
                          </td>
                          <td className="p-4">
                            <button 
                              onClick={() => loadClientHistory(invoice.client_id)}
                              className="text-left hover:text-blue-600 transition-colors"
                            >
                              <p className="font-medium">{invoice.client_name}</p>
                              <p className="text-xs text-slate-500">{invoice.client_email}</p>
                            </button>
                          </td>
                          <td className="p-4">
                            <p className="text-sm">{invoice.service_name}</p>
                          </td>
                          <td className="p-4 text-right">
                            <p className="font-mono">{invoice.total_amount?.toLocaleString('fr-FR')}€</p>
                            {invoice.total_late_fees > 0 && (
                              <p className="text-xs text-orange-600">+{invoice.total_late_fees?.toLocaleString('fr-FR')}€ frais</p>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {getAgeBadge(invoice.days_late)}
                          </td>
                          <td className="p-4 text-right font-mono font-bold text-red-600">
                            {invoice.total_with_fees?.toLocaleString('fr-FR')}€
                          </td>
                          <td className="p-4">
                            {invoice.last_action ? (
                              <div className="text-xs space-y-1">
                                <div className="flex items-center gap-1">
                                  {getActionIcon(invoice.last_action.action_type)}
                                  <span className="font-medium">{getChannelLabel(invoice.last_action.channel || invoice.last_action.action_type)}</span>
                                </div>
                                <p className="text-slate-500">
                                  {new Date(invoice.last_action.created_at).toLocaleDateString('fr-FR')}
                                </p>
                                {invoice.last_action.contact_result && (
                                  <Badge variant="outline" className="text-xs">
                                    {contactResultOptions.find(o => o.value === invoice.last_action.contact_result)?.label || invoice.last_action.contact_result}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <Badge variant="outline" className="bg-gray-100">Aucune action</Badge>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openActionDialog(invoice)}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Relance
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => loadClientHistory(invoice.client_id)}>
                                    <History className="w-4 h-4 mr-2" />
                                    Historique client
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem asChild>
                                    <label className="cursor-pointer flex items-center">
                                      <Upload className="w-4 h-4 mr-2" />
                                      {uploadingPdf[invoice.id] ? 'Upload...' : 'Joindre facture PDF'}
                                      <input
                                        type="file"
                                        accept=".pdf"
                                        className="hidden"
                                        onChange={(e) => handleUploadPdf(invoice.id, e.target.files?.[0])}
                                      />
                                    </label>
                                  </DropdownMenuItem>
                                  {invoice.invoice_pdf_url && (
                                    <DropdownMenuItem asChild>
                                      <a href={`${process.env.REACT_APP_BACKEND_URL}${invoice.invoice_pdf_url}`} target="_blank" rel="noopener noreferrer">
                                        <Download className="w-4 h-4 mr-2" />
                                        Télécharger PDF
                                      </a>
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* TEMPLATES TAB */}
          <TabsContent value="templates">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold">Modèles de messages</h2>
                <p className="text-slate-500">Gérez vos templates de relance et mise en demeure</p>
              </div>
              <Button onClick={() => openTemplateDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau modèle
              </Button>
            </div>

            <div className="grid gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="card">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{template.name}</h3>
                          <Badge variant="outline">{template.channel}</Badge>
                          {template.days_after_due && (
                            <Badge className="bg-slate-100 text-slate-600">J+{template.days_after_due}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          <strong>Objet :</strong> {template.subject}
                        </p>
                        <p className="text-sm text-slate-500 line-clamp-2">
                          {template.content.substring(0, 200)}...
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => openTemplateDialog(template)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteTemplate(template.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Dialog */}
        <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle action de recouvrement</DialogTitle>
              <DialogDescription>
                Enregistrez une relance pour la facture {selectedInvoice?.invoice_number}
              </DialogDescription>
            </DialogHeader>
            
            {selectedInvoice && (
              <div className="space-y-4 py-4">
                {/* Invoice summary */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-mono font-medium">{selectedInvoice.invoice_number}</p>
                      <p className="text-sm text-slate-600">{selectedInvoice.client_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">{selectedInvoice.total_with_fees?.toLocaleString('fr-FR')}€</p>
                      <p className="text-xs text-slate-500">Retard: {selectedInvoice.days_late} jours</p>
                    </div>
                  </div>
                </div>

                {/* Quick template selection */}
                <div className="space-y-2">
                  <Label>Utiliser un modèle</Label>
                  <div className="flex flex-wrap gap-2">
                    {templates.slice(0, 4).map((template) => (
                      <Button
                        key={template.id}
                        type="button"
                        variant={selectedTemplateId === template.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => applyTemplate(template)}
                      >
                        {template.name}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Action type */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type d&apos;action</Label>
                    <Select value={actionType} onValueChange={setActionType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {actionTypeOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="w-4 h-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Contact result (for phone) */}
                  {actionType === 'phone' && (
                    <div className="space-y-2">
                      <Label>Résultat du contact</Label>
                      <Select value={contactResult} onValueChange={setContactResult}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {contactResultOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <div className="flex items-center gap-2">
                                <opt.icon className="w-4 h-4" />
                                {opt.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Subject (for email/mail) */}
                {(actionType === 'email' || actionType === 'registered_mail' || actionType === 'formal_notice') && (
                  <div className="space-y-2">
                    <Label>Objet</Label>
                    <Input
                      value={actionSubject}
                      onChange={(e) => setActionSubject(e.target.value)}
                      placeholder="Objet du message..."
                    />
                  </div>
                )}

                {/* Message content */}
                <div className="space-y-2">
                  <Label>Contenu du message</Label>
                  <Textarea
                    value={actionContent}
                    onChange={(e) => setActionContent(e.target.value)}
                    placeholder="Contenu de la relance..."
                    rows={8}
                  />
                </div>
                
                {/* Internal notes */}
                <div className="space-y-2">
                  <Label>Notes internes</Label>
                  <Textarea
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    placeholder="Notes pour l'équipe (non envoyées au client)..."
                    rows={2}
                  />
                </div>
                
                <Button 
                  onClick={handleCreateAction}
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer l\'action'
                  )}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Client History Dialog */}
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historique de recouvrement
              </DialogTitle>
            </DialogHeader>
            
            {loadingHistory ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : selectedClientHistory && (
              <div className="space-y-6 py-4">
                {/* Client info */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{selectedClientHistory.client?.name}</h3>
                      <p className="text-sm text-slate-500">{selectedClientHistory.client?.email}</p>
                      {selectedClientHistory.client?.phone && (
                        <p className="text-sm text-slate-500">{selectedClientHistory.client?.phone}</p>
                      )}
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-sm text-slate-500">Encours total</p>
                      <p className="text-xl font-bold text-red-600">
                        {selectedClientHistory.total_outstanding?.toLocaleString('fr-FR')}€
                      </p>
                    </div>
                  </div>
                </div>

                {/* Invoices */}
                <div>
                  <h4 className="font-medium mb-3">Factures en cours ({selectedClientHistory.invoices?.length || 0})</h4>
                  <div className="space-y-2">
                    {selectedClientHistory.invoices?.map((inv) => (
                      <div key={inv.id} className="p-3 border rounded-lg flex justify-between items-center">
                        <div>
                          <span className="font-mono font-medium">{inv.invoice_number}</span>
                          <span className="text-sm text-slate-500 ml-2">{inv.service_name}</span>
                        </div>
                        <span className="font-medium">{inv.total_amount?.toLocaleString('fr-FR')}€</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions history */}
                <div>
                  <h4 className="font-medium mb-3">Historique des actions ({selectedClientHistory.actions?.length || 0})</h4>
                  <div className="space-y-3">
                    {selectedClientHistory.actions?.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Aucune action enregistrée</p>
                    ) : (
                      selectedClientHistory.actions?.map((action) => (
                        <div key={action.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getActionIcon(action.action_type)}
                              <span className="font-medium">{getChannelLabel(action.channel || action.action_type)}</span>
                              {action.contact_result && (
                                <Badge variant="outline" className="text-xs">
                                  {contactResultOptions.find(o => o.value === action.contact_result)?.label || action.contact_result}
                                </Badge>
                              )}
                            </div>
                            <span className="text-sm text-slate-500">
                              {new Date(action.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {action.subject && (
                            <p className="text-sm font-medium mb-1">Objet : {action.subject}</p>
                          )}
                          {action.message_content && (
                            <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-3">
                              {action.message_content}
                            </p>
                          )}
                          {action.notes && (
                            <p className="text-xs text-slate-400 mt-2 italic">Note : {action.notes}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            Par {action.performed_by_name || 'Inconnu'} • Facture {action.invoice_number}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Template Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Modifier le modèle' : 'Nouveau modèle de message'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du modèle *</Label>
                  <Input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    placeholder="Ex: Relance 1 - Rappel amical"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Canal</Label>
                  <Select value={templateForm.channel} onValueChange={(v) => setTemplateForm({ ...templateForm, channel: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="mail">Courrier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={templateForm.type} onValueChange={(v) => setTemplateForm({ ...templateForm, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reminder_1">Relance 1</SelectItem>
                      <SelectItem value="reminder_2">Relance 2</SelectItem>
                      <SelectItem value="reminder_3">Relance 3</SelectItem>
                      <SelectItem value="formal_notice">Mise en demeure</SelectItem>
                      <SelectItem value="legal_warning">Avertissement juridique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Jours après échéance</Label>
                  <Input
                    type="number"
                    value={templateForm.days_after_due}
                    onChange={(e) => setTemplateForm({ ...templateForm, days_after_due: e.target.value })}
                    placeholder="Ex: 7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Objet *</Label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Objet du message..."
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Contenu *</Label>
                  <span className="text-xs text-slate-500">
                    Variables : {'{{client_name}}'}, {'{{invoice_number}}'}, {'{{amount}}'}, {'{{due_date}}'}, {'{{days_late}}'}, {'{{total_with_fees}}'}
                  </span>
                </div>
                <Textarea
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                  placeholder="Contenu du message..."
                  rows={10}
                />
              </div>

              <Button onClick={handleSaveTemplate} className="w-full" disabled={savingTemplate}>
                {savingTemplate && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingTemplate ? 'Mettre à jour' : 'Créer le modèle'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default RecoveryManagementPage;
